import { Button, Popconfirm, message, Spin } from 'antd';
import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '@ant-design/icons';
import { cloneDeep } from 'lodash';

import MetricTopInfo from 'emp-3.0/components/Metrics/IndividualMetric/MetricTopInfo';
import { useCreatePermissions } from '@src/hooks/usePermissions';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import {
  useDeleteFlowById,
  useDeploy,
  useGetFlowList,
  useUndeploy,
} from '@src/emp-3.0/api/queryHooks';
import FlowForm from '@src/emp-3.0/components/Metrics/Flow/FlowForm';
import {
  renderDeployedStatusDeletePrompt,
  renderDeployPrompt,
  renderUndeployPrompt,
} from '@src/emp-3.0/components/Management/components/CellRenderers/ActionConfirmButton';
import { ContentWrapper, Divider, Layout, MetricName, Title, TopPanel } from '../../styled';
import { useMetricBuilderNavigate } from '@src/pages/metric-templates/hooks';
import EditSvg from '@icons/event-management/edit.svg';
import UndeploySvg from '@icons/emp/undeploy.svg';
import DeploySvg from '@icons/emp/deploy.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { isDeployed } from '@src/emp-3.0/utils';
import { useWorkflowStore } from '@src/pages/workflow/store';
import { EFormMode } from '@src/emp-3.0/types/common';
import { useFlowStore } from '@src/pages/workflow/store/flowBuilder';
import useScreenSize from '@hooks/useScreenSize';
import { workbenchModeTrueButNotWorkbenchAsset } from '@src/emp-3.0/components/WorkbenchButton/util';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';
import { useIsPatternMode } from '../../utils';

interface IMetricsBuilderProps {
  onDelete?: () => void;
  onDeploy?: () => void;
  onUndeploy?: () => void;
}

const FlowDetail: React.FC<IMetricsBuilderProps> = ({ onDelete, onDeploy, onUndeploy }) => {
  const {
    navigateMetricBuilderIndex,
    navigateFlowDetailPage,
    navigateMetricBuilderFlowPage,
    navigatePatternDetailPage,
  } = useMetricBuilderNavigate();
  const { flowId } = useParams<any>();
  const { workbenchMode } = useInitialInfoStore();
  const hasCreatePermission = useCreatePermissions();
  const { screenWidth } = useScreenSize();
  const { isPatternMode, patternCreatePermission } = useIsPatternMode();
  const { setFormMode } = useWorkflowStore();
  const { setActiveSectionKey, resetAll } = useFlowStore();

  const { mutate: deleteFlow, isLoading: isDelteFlowLoaidng } = useDeleteFlowById();
  const {
    data: flowList = [],
    isLoading: isGetFlowListLoading,
    isFetching: isFetchingFlowList,
  } = useGetFlowList();
  const { mutate: deployFlow, isLoading: isLoadingDeploy } = useDeploy();
  const { mutate: undeployFlow, isLoading: isLoadingUndeploy } = useUndeploy();

  const flowData = useMemo(() => {
    return cloneDeep(flowList.find((x) => x.id === Number(flowId)));
  }, [flowId, flowList]);

  const isDeployedStatus = isDeployed(flowData);

  useEffect(() => {
    setFormMode(EFormMode.VIEW);
  }, []);
  useEffect(() => {
    resetAll();
    setActiveSectionKey(['0', '1', '2', '3']);
  }, [flowId]);

  useEffect(() => {
    if (!isFetchingFlowList && flowId) {
      if (flowList.length === 0) {
        navigateMetricBuilderIndex({ replace: true });
      } else {
        const flow = flowList.find((metric) => metric.id === Number(flowId));
        if (!flow) {
          navigateMetricBuilderFlowPage(flowList[0].id);
        }
      }
    }
  }, [
    flowId,
    flowList,
    isFetchingFlowList,
    navigateMetricBuilderFlowPage,
    navigateMetricBuilderIndex,
  ]);

  const handleDeploy = () => {
    if (flowId) {
      deployFlow(
        {
          resource: 'flow',
          payload: {
            deploy_all: false,
            ids: [Number(flowId)],
          },
        },
        {
          onSuccess: () => {
            message.success('Deploy success!');
            onDeploy && onDeploy();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
            message.error(errorMessage);
          },
        },
      );
    }
  };

  const handleUndeploy = () => {
    if (flowId) {
      undeployFlow(
        {
          resource: 'flow',
          payload: {
            ids: [Number(flowId)],
          },
        },
        {
          onSuccess: () => {
            message.success('Undeploy success!');
            onUndeploy && onUndeploy();
          },
          onError: (error: any) => {
            const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
            message.error(errorMessage);
          },
        },
      );
    }
  };

  const handleDelete = () => {
    if (flowId) {
      deleteFlow(Number(flowId), {
        onSuccess: () => {
          message.success('Delete success!');
          onDelete && onDelete();
          navigateMetricBuilderIndex({ replace: true });
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.msg || error?.response?.data?.message;
          message.error(errorMessage);
        },
      });
    }
  };

  const isLoading = isGetFlowListLoading || isLoadingDeploy || isLoadingUndeploy;

  return (
    <Layout>
      <TopPanel>
        <Title>
          {isPatternMode ? 'Pattern:' : 'Flow:'}
          <span style={{ width: '8px' }} />
          <MetricName>
            <AddEllipsisWithTooltip text={flowData?.name || ''} dataIndex="0" />
          </MetricName>
        </Title>
        {hasCreatePermission && !workbenchModeTrueButNotWorkbenchAsset(flowData?.is_beta) ? (
          <Divider>
            <Button
              icon={<Icon component={EditSvg} />}
              type="primary"
              ghost
              disabled={isPatternMode && !patternCreatePermission}
              onClick={() => {
                if (flowData?.flow_definition_type === 'sql') {
                  navigatePatternDetailPage(flowId);
                } else {
                  navigateFlowDetailPage(flowId);
                }
              }}
              size="small"
            >
              Edit
            </Button>
            <div style={{ display: 'flex', gap: '8px' }}>
              {!workbenchMode && (
                <Popconfirm
                  title={isDeployedStatus ? renderUndeployPrompt() : renderDeployPrompt()}
                  onConfirm={() => (isDeployedStatus ? handleUndeploy() : handleDeploy())}
                  okText={isDeployedStatus ? 'Undeploy' : 'Deploy'}
                  cancelText="Cancel"
                  placement="topRight"
                  okButtonProps={{
                    danger: isDeployedStatus,
                  }}
                >
                  <Button
                    loading={isLoading}
                    disabled={isDelteFlowLoaidng}
                    icon={<Icon component={isDeployedStatus ? UndeploySvg : DeploySvg} />}
                    type="primary"
                    ghost
                    danger={isDeployedStatus}
                    size="small"
                  >
                    {isDeployedStatus ? 'Undeploy' : 'Deploy'}
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title={renderDeployedStatusDeletePrompt()}
                onConfirm={handleDelete}
                okText="Delete"
                cancelText="Cancel"
                placement="topRight"
                okButtonProps={{
                  danger: true,
                }}
              >
                <Button
                  icon={<Icon component={DeleteSvg} />}
                  type="primary"
                  danger
                  loading={isDelteFlowLoaidng}
                  disabled={isLoading}
                  ghost
                  size="small"
                >
                  Delete
                </Button>
              </Popconfirm>
            </div>
          </Divider>
        ) : null}
      </TopPanel>
      <ContentWrapper>
        <MetricTopInfo metricId={Number(flowId)} type="flow" />
        {isLoading ? (
          <Spin spinning={isLoading} />
        ) : (
          <div style={{ display: 'flex', marginLeft: '16px' }}>
            <FlowForm mode={'view'} flow={flowData} formProps={{ style: { flex: 1 } }} />
            {screenWidth < 1440 ? null : <div style={{ width: '35%' }} />}
          </div>
        )}
      </ContentWrapper>
    </Layout>
  );
};

export default FlowDetail;
